import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, onIdTokenChanged, signOut } from "firebase/auth";
import PropTypes from "prop-types";
import Cookies from "js-cookie";
import { auth } from "../firebase";
import { clearSsoSession, getSsoToken, getSsoUser } from "../authService";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const setSsoUserIfAvailable = useCallback(() => {
    const ssoUser = getSsoUser();
    if (!ssoUser) return false;

    setUser(ssoUser);
    setLoading(false);
    return true;
  }, []);

  useEffect(() => {
    
    // NO establecer loading = false en la verificación inicial
    // Esperar a que onAuthStateChanged se ejecute para mantener el skeleton visible
    // durante toda la verificación de Firebase
    
    // Timeout de seguridad: si después de 3 segundos no se ejecuta onAuthStateChanged,
    // forzar loading = false para evitar que la app se quede congelada
    const safetyTimeout = setTimeout(() => {
      console.warn("⚠️ [AuthContext] Timeout de seguridad: onAuthStateChanged no se ejecutó en 3 segundos");
      if (!setSsoUserIfAvailable()) {
        setLoading(prevLoading => {
          if (prevLoading) {
            return false;
          }
          return prevLoading;
        });
      }
    }, 3000);
    
    // Listener para cambios en el estado de autenticación (login/logout)
    // Este listener se ejecutará cuando Firebase termine de verificar el estado
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      // Limpiar el timeout de seguridad ya que onAuthStateChanged se ejecutó
      clearTimeout(safetyTimeout);
      
      
      // Si hay una bandera de logout intencional y se detecta un usuario, no restaurarlo
      const logoutIntentional = localStorage.getItem("logout_intentional");
      if (logoutIntentional === "true") {
        if (firebaseUser) {
          // Forzar signOut para asegurar que Firebase también limpie el estado
          try {
            await signOut(auth);
          } catch (error) {
            console.error("❌ [AuthContext] Error al forzar signOut:", error);
          }
        }
        // Limpiar todos los datos de autenticación
        localStorage.removeItem("token");
        localStorage.removeItem("authToken");
        localStorage.removeItem("iOS_token");
        localStorage.removeItem("userId");
        localStorage.removeItem("firebaseUid");
        clearSsoSession();
        // Limpiar la bandera después de procesarla para evitar conflictos en el siguiente login
        localStorage.removeItem("logout_intentional");
        setUser(null);
        setLoading(false);
        return;
      }

      if (firebaseUser) {
        clearSsoSession();
        Cookies.remove("userId");
        localStorage.removeItem("userId");
        localStorage.removeItem("firebaseUid");

        try {
          // Obtener token fresco al detectar usuario
          // Usar forceRefresh = false para usar cache si está disponible, pero asegurar que se obtenga
          const token = await firebaseUser.getIdToken(false);
          // Guardar el token en todas las keys necesarias para compatibilidad
          localStorage.setItem("token", token);
          localStorage.setItem("authToken", token);
          localStorage.setItem("iOS_token", token);
          localStorage.setItem("firebaseUid", firebaseUser.uid);
          setUser(firebaseUser);
        } catch (error) {
          console.error("❌ [AuthContext] Error al obtener token:", error);
          // Si hay error pero hay token en localStorage, mantenerlo
          const fallbackToken = localStorage.getItem("authToken") || localStorage.getItem("token");
          if (fallbackToken) {
            setUser(firebaseUser);
          } else {
            setUser(null);
            // Limpiar tokens si hay error y no hay fallback
            localStorage.removeItem("token");
            localStorage.removeItem("authToken");
            localStorage.removeItem("iOS_token");
          }
        } finally {
          // SIEMPRE establecer loading = false, incluso si hay errores
          setLoading(false);
        }
        return;
      }

      if (setSsoUserIfAvailable()) {
        return;
      }

      // Solo limpiar tokens si no hay token válido en localStorage
      const existingToken = localStorage.getItem("authToken") || localStorage.getItem("token");
      if (!existingToken) {
        localStorage.removeItem("token");
        localStorage.removeItem("authToken");
        localStorage.removeItem("iOS_token");
      }
      setUser(null);
      setLoading(false);
    });

    const handleSsoAuthenticated = () => {
      setSsoUserIfAvailable();
    };

    window.addEventListener("payflex-sso-authenticated", handleSsoAuthenticated);

    // Listener para cambios en el token (renovación automática)
    // Esto es crucial: Firebase renueva tokens automáticamente cada ~55 minutos
    const unsubscribeToken = onIdTokenChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const token = await firebaseUser.getIdToken(false); // false = usar cache si es válido
          // Actualizar localStorage con el token renovado
          localStorage.setItem("token", token);
          localStorage.setItem("authToken", token);
          localStorage.setItem("iOS_token", token);
        } catch (error) {
          console.error("❌ [AuthContext] Error al obtener token renovado:", error);
        }
      }
    });

    return () => {
      clearTimeout(safetyTimeout);
      window.removeEventListener("payflex-sso-authenticated", handleSsoAuthenticated);
      unsubscribeAuth();
      unsubscribeToken();
    };
  }, [setSsoUserIfAvailable]);

  // Función para obtener el token de Firebase (siempre fresco)
  // Si forceRefresh es true, fuerza la renovación del token
  const getToken = async (forceRefresh = false) => {
    // Primero verificar si hay un usuario en auth.currentUser (más confiable)
    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        // Obtener token fresco (forceRefresh = true fuerza renovación)
        const token = await currentUser.getIdToken(forceRefresh);
        // Actualizar localStorage
        localStorage.setItem("token", token);
        localStorage.setItem("authToken", token);
        localStorage.setItem("iOS_token", token);
        return token;
      } catch (error) {
        console.error("❌ [AuthContext] Error al obtener token:", error);
        // Si hay error pero hay token en localStorage, usarlo como fallback temporal
        const fallbackToken = localStorage.getItem("authToken") || localStorage.getItem("token");
        if (fallbackToken) {
          return fallbackToken;
        }
        return null;
      }
    }
    if (user?.isSso) {
      return getSsoToken();
    }

    // Si no hay currentUser pero hay user en el estado, intentar con ese
    if (user && typeof user.getIdToken === "function") {
      try {
        const token = await user.getIdToken(forceRefresh);
        localStorage.setItem("token", token);
        localStorage.setItem("authToken", token);
        localStorage.setItem("iOS_token", token);
        return token;
      } catch (error) {
        console.error("❌ [AuthContext] Error al obtener token:", error);
        // Si hay error pero hay token en localStorage, usarlo como fallback temporal
        const fallbackToken = localStorage.getItem("authToken") || localStorage.getItem("token");
        if (fallbackToken) {
          return fallbackToken;
        }
        return null;
      }
    }
    
    // Si no hay usuario autenticado en Firebase pero hay token en localStorage,
    // puede ser que Firebase aún no haya actualizado el estado después del login.
    // Esperar un poco más antes de usar el fallback para dar tiempo a que Firebase se sincronice.
    const fallbackToken = localStorage.getItem("authToken") || localStorage.getItem("token");
    if (fallbackToken) {
      // Esperar más tiempo y verificar múltiples veces
      for (let i = 0; i < 5; i++) {
        await new Promise(resolve => setTimeout(resolve, 200));
        const retryUser = auth.currentUser;
        if (retryUser) {
          try {
            const token = await retryUser.getIdToken(false);
            localStorage.setItem("token", token);
            localStorage.setItem("authToken", token);
            localStorage.setItem("iOS_token", token);
            return token;
          } catch (error) {
            console.warn(`⚠️ [AuthContext] Intento ${i + 1}/5: Error al obtener token, reintentando...`);
          }
        }
      }
      // Solo usar el fallback si después de todos los intentos no hay usuario
      console.warn("⚠️ [AuthContext] Usando token de localStorage como fallback después de esperar sincronización");
      return fallbackToken;
    }

    const ssoToken = getSsoToken();
    if (ssoToken) {
      return ssoToken;
    }
    
    return null;
  };


  return (
    <AuthContext.Provider value={{ user, loading, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

// Typechecking props for the AuthProvider
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    console.error("❌ [AuthContext] useAuth debe usarse dentro de AuthProvider");
  }
  return context;
}
